/* =====================================================
   FOCUS — script.js
   Luxury Anti Blue Light Glasses — Shopify Theme
   ===================================================== */

'use strict';

/* ------------------- UTILITIES ------------------- */
const select   = (sel, ctx = document) => ctx.querySelector(sel);
const selectAll = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);


/* ------------------- STICKY HEADER ------------------- */
(function initHeader() {
  const header = select('#site-header');
  if (!header) return;

  let ticking = false;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        header.classList.toggle('scrolled', window.scrollY > 30);
        ticking = false;
      });
      ticking = true;
    }
  }

  on(window, 'scroll', onScroll, { passive: true });
  onScroll(); // run on load
})();


/* ------------------- MOBILE MENU ------------------- */
(function initMobileMenu() {
  const toggle  = select('#nav-toggle');
  const menu    = select('#mobile-menu');
  const burgers = selectAll('#nav-toggle span');
  if (!toggle || !menu) return;

  let isOpen = false;

  function toggleMenu() {
    isOpen = !isOpen;
    menu.classList.toggle('is-open', isOpen);
    toggle.setAttribute('aria-expanded', isOpen);

    // Animate hamburger → X
    if (isOpen) {
      burgers[0].style.transform = 'translateY(7px) rotate(45deg)';
      burgers[1].style.opacity   = '0';
      burgers[2].style.transform = 'translateY(-7px) rotate(-45deg)';
    } else {
      burgers[0].style.transform = '';
      burgers[1].style.opacity   = '';
      burgers[2].style.transform = '';
    }
  }

  on(toggle, 'click', toggleMenu);

  // Close on menu link click
  selectAll('#mobile-menu a').forEach(link => {
    on(link, 'click', () => { if (isOpen) toggleMenu(); });
  });

  // Close on Escape
  on(document, 'keydown', e => {
    if (e.key === 'Escape' && isOpen) toggleMenu();
  });
})();


/* ------------------- INTERSECTION OBSERVER: SCROLL ANIMATIONS ------------------- */
(function initScrollAnimations() {
  const elements = selectAll('[data-animate], .benefit-card, .product-card, .testimonial-card');
  if (!elements.length) return;

  function revealEl(el) {
    if (el.classList.contains('is-visible')) return;
    const delay = parseInt(el.dataset.delay || 0, 10);
    setTimeout(() => el.classList.add('is-visible'), delay);
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      revealEl(entry.target);
      observer.unobserve(entry.target);
    });
  }, {
    threshold: 0.05,
    rootMargin: '0px 0px 0px 0px'
  });

  elements.forEach(el => observer.observe(el));

  // Failsafe: force-reveal all hidden elements after 2.5s
  // This handles fast scrollers and slow devices where the observer may miss elements
  setTimeout(() => {
    elements.forEach(el => revealEl(el));
  }, 2500);
})();


/* ------------------- LIFESTYLE BARS ANIMATION ------------------- */
(function initLifestyleBars() {
  const bars = selectAll('.lc-bar-fill');
  if (!bars.length) return;

  // Store the target width from inline style, then reset to 0
  bars.forEach(bar => {
    const target = bar.style.width;
    bar.dataset.targetWidth = target;
    bar.style.width = '0';
  });

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const bar = entry.target;
      // Slight delay for visual delight
      setTimeout(() => {
        bar.style.width = bar.dataset.targetWidth;
      }, 200);
      observer.unobserve(bar);
    });
  }, { threshold: 0.5 });

  bars.forEach(bar => observer.observe(bar));

  // Failsafe
  setTimeout(() => {
    bars.forEach(bar => {
      if (bar.style.width === '0px' || bar.style.width === '0') {
        bar.style.width = bar.dataset.targetWidth;
      }
    });
  }, 2500);
})();


/* ------------------- PARALLAX HERO GLOW ------------------- */
(function initParallaxGlow() {
  const glow = select('.hero-glow');
  if (!glow) return;

  let mouseX = 0, mouseY = 0;
  let currentX = 0, currentY = 0;
  let rafId;

  function lerp(a, b, t) { return a + (b - a) * t; }

  function animate() {
    currentX = lerp(currentX, mouseX, 0.06);
    currentY = lerp(currentY, mouseY, 0.06);
    glow.style.transform = `translate(calc(-50% + ${currentX * 0.02}px), calc(-50% + ${currentY * 0.02}px))`;
    rafId = requestAnimationFrame(animate);
  }

  on(document, 'mousemove', e => {
    mouseX = e.clientX - window.innerWidth / 2;
    mouseY = e.clientY - window.innerHeight / 2;
  }, { passive: true });

  animate();

  // Cleanup on page hide
  on(document, 'visibilitychange', () => {
    if (document.hidden) cancelAnimationFrame(rafId);
    else animate();
  });
})();


/* ------------------- GLASSES TILT ON MOUSE MOVE ------------------- */
(function initGlassesTilt() {
  const glasses = select('#hero-glasses');
  const hero    = select('.hero');
  if (!glasses || !hero) return;

  let targetRX = 0, targetRY = 0;
  let currentRX = 0, currentRY = 0;

  function lerp(a, b, t) { return a + (b - a) * t; }

  function updateTilt() {
    currentRX = lerp(currentRX, targetRX, 0.07);
    currentRY = lerp(currentRY, targetRY, 0.07);
    glasses.style.transform = `rotateX(${currentRX}deg) rotateY(${currentRY}deg)`;
    requestAnimationFrame(updateTilt);
  }

  on(hero, 'mousemove', e => {
    const rect = hero.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    targetRY =  x * 12;
    targetRX = -y * 8;
  }, { passive: true });

  on(hero, 'mouseleave', () => {
    targetRX = 0;
    targetRY = 0;
  });

  updateTilt();
})();


/* ------------------- CURSOR GLOW TRAIL ------------------- */
(function initCursorGlow() {
  // Only on non-touch devices
  if (window.matchMedia('(pointer: coarse)').matches) return;

  const glow = document.createElement('div');
  glow.className = 'cursor-glow';
  glow.style.cssText = `
    position: fixed;
    pointer-events: none;
    z-index: 9999;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,106,0,0.06) 0%, transparent 70%);
    transform: translate(-50%, -50%);
    transition: opacity 0.3s ease;
    top: 0; left: 0;
  `;
  document.body.appendChild(glow);

  let mouseX = -500, mouseY = -500;
  let curX = -500, curY = -500;

  function lerp(a, b, t) { return a + (b - a) * t; }

  function moveCursor() {
    curX = lerp(curX, mouseX, 0.12);
    curY = lerp(curY, mouseY, 0.12);
    glow.style.left = curX + 'px';
    glow.style.top  = curY + 'px';
    requestAnimationFrame(moveCursor);
  }

  on(document, 'mousemove', e => {
    mouseX = e.clientX;
    mouseY = e.clientY;
    glow.style.opacity = '1';
  }, { passive: true });

  on(document, 'mouseleave', () => { glow.style.opacity = '0'; });

  moveCursor();
})();


/* ------------------- PRODUCT CARD SPOTLIGHT ------------------- */
(function initCardSpotlight() {
  selectAll('.product-card, .benefit-card').forEach(card => {
    on(card, 'mousemove', e => {
      const rect = card.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width)  * 100;
      const y = ((e.clientY - rect.top)  / rect.height) * 100;
      card.style.setProperty('--mouse-x', `${x}%`);
      card.style.setProperty('--mouse-y', `${y}%`);
      card.style.backgroundImage =
        `radial-gradient(circle at ${x}% ${y}%, rgba(255,106,0,0.04) 0%, transparent 60%), var(--bg, transparent)`;
    }, { passive: true });

    on(card, 'mouseleave', () => {
      card.style.backgroundImage = '';
    });
  });
})();


/* ------------------- NUMBER COUNT-UP ANIMATION ------------------- */
(function initCountUp() {
  const statNums = selectAll('.stat-num, .trust-num');

  function parseNum(str) {
    const cleaned = str.replace(/[^0-9.]/g, '');
    return parseFloat(cleaned) || 0;
  }

  function formatNum(original, current) {
    if (original.includes('%')) return Math.round(current) + '%';
    if (original.includes('K')) return (current / 1000).toFixed(0) + 'K+';
    if (original.includes('★')) return current.toFixed(1) + '★';
    if (original.includes('/')) {
      const parts = original.split('/');
      return current.toFixed(1) + '/' + parts[1];
    }
    return Math.round(current).toLocaleString();
  }

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      const original = el.textContent.trim();
      const end = parseNum(original);
      if (!end) return;

      // Start from 85% of the target so numbers look realistic from the first frame
      const startVal = end * 0.85;
      const duration = 900;
      const start    = performance.now();

      function step(now) {
        const elapsed  = now - start;
        const progress = Math.min(elapsed / duration, 1);
        // Ease out cubic
        const eased    = 1 - Math.pow(1 - progress, 3);
        el.textContent = formatNum(original, startVal + (end - startVal) * eased);
        if (progress < 1) requestAnimationFrame(step);
        else el.textContent = original; // restore exact original
      }

      requestAnimationFrame(step);
      observer.unobserve(el);
    });
  }, { threshold: 0.5 });

  statNums.forEach(el => observer.observe(el));
})();


/* ------------------- SMOOTH ANCHOR SCROLL ------------------- */
(function initSmoothScroll() {
  selectAll('a[href^="#"]').forEach(link => {
    on(link, 'click', e => {
      const href   = link.getAttribute('href');
      const target = href === '#' ? null : select(href);
      if (!target) return;
      e.preventDefault();
      const offset = 80; // nav height
      const top = target.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    });
  });
})();


/* ------------------- CART NOTIFICATION TOAST ------------------- */
(function initCartToast() {
  // Listen for Shopify AJAX cart events (if using AJAX cart)
  document.addEventListener('cart:item-added', () => {
    const toast = document.createElement('div');
    toast.className = 'cart-toast';
    toast.innerHTML = `
      <span class="toast-icon">◈</span>
      <span>Added to cart</span>
      <a href="/cart" class="toast-link">View Cart →</a>
    `;
    toast.style.cssText = `
      position: fixed;
      bottom: 30px;
      right: 30px;
      background: #0f0f0f;
      border: 1px solid rgba(255,106,0,0.3);
      border-radius: 8px;
      padding: 16px 22px;
      display: flex;
      align-items: center;
      gap: 12px;
      font-family: 'Barlow Condensed', sans-serif;
      font-size: 14px;
      letter-spacing: 0.06em;
      color: #fff;
      z-index: 9999;
      box-shadow: 0 8px 40px rgba(0,0,0,0.6), 0 0 30px rgba(255,106,0,0.08);
      animation: slideUp 0.4s cubic-bezier(0.23,1,0.32,1) forwards;
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'fadeOut 0.4s ease forwards';
      setTimeout(() => toast.remove(), 400);
    }, 3000);
  });

  // Inject keyframes
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(20px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeOut {
      from { opacity: 1; }
      to   { opacity: 0; transform: translateY(10px); }
    }
    .toast-icon { color: #ff6a00; }
    .toast-link { color: #ff6a00; margin-left: 8px; }
    .toast-link:hover { text-decoration: underline; }
  `;
  document.head.appendChild(style);
})();


/* ------------------- PAGE LOAD REVEAL ------------------- */
(function initPageReveal() {
  document.body.style.opacity = '0';
  document.body.style.transition = 'opacity 0.5s ease';

  window.addEventListener('load', () => {
    document.body.style.opacity = '1';
  });

  // Failsafe: if load event never fires or takes too long, show page after 1.5s
  setTimeout(() => {
    document.body.style.opacity = '1';
  }, 1500);
})();
